/**
 * Payroll Reconciliation Card for Accountant Report View
 * Displays payroll reconciliation data and variance analysis
 */

import React from 'react';
import { Users, AlertTriangle } from 'lucide-react';
import CommentsSection from '../CommentsSection';

interface PayrollReconciliation {
  pl_total_2022?: number;
  variance_pct?: number;
  reconciled?: boolean;
  recommendation?: string;
}

interface PayrollReconciliationCardProps {
  payrollReconciliation: PayrollReconciliation;
  reportId: string;
  canComment: boolean;
  getAuthHeader: () => { Authorization: string } | {};
  formatCurrency: (amount?: number) => string;
  formatPercent: (value?: number) => string;
}

export default function PayrollReconciliationCard({
  payrollReconciliation,
  reportId,
  canComment,
  getAuthHeader,
  formatCurrency,
  formatPercent
}: PayrollReconciliationCardProps) {
  if (!payrollReconciliation) return null;

  return (
    <div
      className="rounded-xl shadow-sm p-6 mb-6"
      style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
    >
      <h2 className="text-xl font-bold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
        <Users size={22} className="mr-2" />
        Payroll Reconciliation
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div
          className="rounded-lg p-4"
          style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
        >
          <p className="text-sm mb-2 font-medium" style={{ color: 'var(--color-needs-human-text)' }}>P&L Monthly Total (2022)</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
            {formatCurrency(payrollReconciliation.pl_total_2022)}
          </p>
        </div>
        <div
          className="rounded-lg p-4"
          style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
        >
          <p className="text-sm mb-2 font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Variance</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
            {formatPercent(payrollReconciliation.variance_pct)}
          </p>
          <p
            className="text-sm mt-2 font-medium flex items-center gap-1"
            style={{ color: payrollReconciliation.reconciled ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}
          >
            {payrollReconciliation.reconciled ? '✓ Reconciled' : <><AlertTriangle size={14} /> Variance Detected</>}
          </p>
        </div>
      </div>
      {payrollReconciliation.recommendation && (
        <div className="mt-4 rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{payrollReconciliation.recommendation}</p>
        </div>
      )}
      
      {canComment && (
        <CommentsSection
          reportId={reportId}
          section="payroll_reconciliation"
          sectionTitle="Payroll Reconciliation"
          getAuthHeader={getAuthHeader}
          canComment={true}
        />
      )}
    </div>
  );
}
